import React, { useEffect, useState } from 'react';
import { InventoryItem, ItemType, Currency } from '../../types/item';
import { BarcodeTemplate } from '../../types/ui';
import { getCurrentExchangeRate, convertTryToUsd } from '../../services/exchangeRateService';
import { generateDataMatrixSVG } from '../../utils';

interface BarcodeLabelProps {
  barcode: string;
  onLoad?: () => void;
  item?: InventoryItem;
  template?: BarcodeTemplate;
}

/**
 * Barcode label component for rendering a printable barcode label
 * Uses a foldable 72mm x 10mm design with the following structure:
 * - Left 20mm: Side A (printable, always shows barcode)
 * - Middle 20mm: Side B (printable, content depends on template)
 * - Right 32mm: Tail (non-printable, used for threading or wrapping)
 * - Only the first 40mm (Side A + Side B) is printable
 * 
 * Available templates:
 * - Template A: Mirrored barcode on both sides
 * - Template B: Barcode on front, blank on back
 * - Template C: Barcode on front, item details on back
 * - Template D: Barcode on front, USD price on back
 */
const BarcodeLabel: React.FC<BarcodeLabelProps> = ({ barcode, onLoad, item, template = BarcodeTemplate.A }) => {
  const [currentExchangeRate, setCurrentExchangeRate] = useState<number>(0);
  const [dataMatrixSvg, setDataMatrixSvg] = useState<string>('');
  const [generatingSvg, setGeneratingSvg] = useState<boolean>(false);

  // Fetch current exchange rate when needed
  useEffect(() => {
    if (
      (template === BarcodeTemplate.D && item?.sellCurrency === Currency.TRY) ||
      (template === BarcodeTemplate.A && item?.sellCurrency === Currency.TRY) // Added condition for Template A
    ) {
      getCurrentExchangeRate().then(rate => {
        setCurrentExchangeRate(rate);
      }).catch(err => {
        console.error('Error fetching exchange rate:', err);
      });
    }
  }, [template, item]);

  useEffect(() => {
    if (barcode) {
      setGeneratingSvg(true);
      setDataMatrixSvg('');
      generateDataMatrixSVG(barcode, 10)
        .then(svg => {
          setDataMatrixSvg(svg);
          if (onLoad) onLoad();
        })
        .catch(err => {
          console.error('Error generating barcode SVG for preview:', err);
          if (onLoad) onLoad();
        })
        .finally(() => {
          setGeneratingSvg(false);
        });
    }
  }, [barcode, onLoad]);

  // Format USD price for Template D
  const getUsdPrice = () => {
    if (!item) return null;

    let priceText = '';
    if (item.sellCurrency === Currency.USD) {
      priceText = `$${item.sellPerUnit.toFixed(2)}`;
    } else if (item.sellCurrency === Currency.TRY) {
      if (currentExchangeRate > 0) {
        const usdPrice = convertTryToUsd(item.sellPerUnit, currentExchangeRate);
        priceText = `$${usdPrice.toFixed(2)}`;
      } else {
        priceText = 'USD Fiyat Bekleniyor'; // Or some loading/error state
      }
    } else {
        priceText = 'USD Fiyat Belirlenmedi';
    }

    // Apply specific styling for Template D price
    return (
      <div style={{
        fontSize: '11pt', // Increased font size
        fontWeight: 'bold',
        textAlign: 'center',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: '1' // Adjust line height to help with vertical centering for single line
      }}>
        {priceText}
      </div>
    );
  };

  // Format item details for Side B based on item type and template
  const renderItemDetails = () => {
    if (template === BarcodeTemplate.A) {
      return null; // Template A uses mirrored barcode, handled separately
    }
    
    if (template === BarcodeTemplate.B) {
      return null; // Template B is blank on Side B
    }
    
    if (template === BarcodeTemplate.D) {
      return getUsdPrice();
    }
    
    // Template C - show item details
    if (template === BarcodeTemplate.C && item) {
      const currencySymbol = item.sellCurrency === Currency.TRY ? '₺' : '$';
      
      if (item.type === ItemType.SILVER) {
        return (
          <>
            <div>Ağırlık: {item.weight.toFixed(2)}gr</div>
            <div>Fiyat/gr: {item.pricePerGram.toFixed(2)}{currencySymbol}</div>
            <div>Satış: {item.sellPerUnit.toFixed(2)}{currencySymbol}</div>
          </>
        );
      } else if (item.type === ItemType.WATCH) {
        return (
          <>
            {item.brand && <div>Marka: {item.brand}</div>}
            <div>Satış: {item.sellPerUnit.toFixed(2)}{currencySymbol}</div>
          </>
        );
      }
    }

    return null;
  };

  // Function to render the Data Matrix SVG with appropriate styling for preview
  const renderDataMatrixPreview = () => {
    if (generatingSvg) {
      return <div className="text-xs text-gray-500">(Loading...)</div>;
    }
    if (!dataMatrixSvg) {
      return <div className="text-xs text-red-500">(Error)</div>;
    }
    // Use dangerouslySetInnerHTML to render the SVG string
    // Set container size closer to the target print size (7.2mm)
    // Let the SVG scale within this container
    return (
      <div
        style={{ 
          width: '7.2mm', 
          height: '7.2mm', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }} // Set container size
        dangerouslySetInnerHTML={{ __html: dataMatrixSvg.replace(/<svg /,'<svg width="100%" height="100%" ') }} // Ensure SVG scales
      />
    );
  };

  // Updated renderSideB to use the SVG string for Template A and handle Template E
  const renderSideB = () => {
    if (template === BarcodeTemplate.A) {
      // Template A: NEW LAYOUT - Side B is USD Price
      return getUsdPrice(); // getUsdPrice already returns styled price info
    }

    // Template E: Render barcode ID string
    if (template === BarcodeTemplate.E) {
      return (
        <div style={{
          fontSize: '9pt', // Increased font size for better readability
          lineHeight: '1.1',
          fontFamily: 'Arial, sans-serif',
          textAlign: 'center',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center', // Vertically center
          justifyContent: 'center', // Horizontally center
          paddingLeft: '0.75mm' // Maintain padding from other templates
        }}>
          {barcode} {/* Display the barcode ID string */}
        </div>
      );
    }

    // For Template D, renderItemDetails will return the styled price from getUsdPrice
    // For other templates (B, C), use the default styling.
    if (template === BarcodeTemplate.D) {
      return renderItemDetails(); // This will now return the div with specific styles for Template D
    }

    // Templates B, C: Either blank or showing details
    return (
      <div style={{
        fontSize: '7pt',
        lineHeight: '1.1',
        fontFamily: 'Arial, sans-serif',
        textAlign: 'center',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        paddingLeft: '0.75mm'
      }}>
        {renderItemDetails()}
      </div>
    );
  };

  return (
    <div className="barcode-label-container" style={{ 
      width: '72mm', 
      height: '10mm', 
      display: 'flex',
      boxSizing: 'border-box'
    }}>
      {/* Side A - Left 20mm - Always shows Data Matrix */}
      <div className="barcode-side" style={{ 
        width: '20mm', 
        height: '10mm', 
        display: 'flex', 
        // flexDirection and alignItems removed to be handled by content or specific template style
        // alignItems: 'center',
        // justifyContent: 'center',
        boxSizing: 'border-box',
        padding: '0',
        borderRight: '1px dashed #ccc',
        overflow: 'hidden'
      }}>
        {/* Render Side A content based on template */}
        {template === BarcodeTemplate.A ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', width: '100%', height: '100%', paddingLeft: '0.5mm', boxSizing: 'border-box' }}>
            <div style={{ flexShrink: 0, marginRight: '1mm' }}>
              {renderDataMatrixPreview()}
            </div>
            <div style={{ fontSize: '6pt', fontFamily: 'Arial, sans-serif', textAlign:'left', lineHeight: '1', overflow: 'hidden', flexGrow: 1, paddingRight:'0.5mm' }}>
              {barcode}
            </div>
          </div>
        ) : (
          // Default for other templates: centered Data Matrix
          <div style={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
            {renderDataMatrixPreview()}
          </div>
        )}
      </div>

      {/* Side B - Middle 20mm - Content varies by template */}
      <div className="barcode-side" style={{ 
        width: '20mm', 
        height: '10mm', 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        boxSizing: 'border-box',
        padding: '0',
        paddingLeft: '0.75mm',
        borderRight: '1px dashed #ccc',
        overflow: 'hidden'
      }}>
        {renderSideB()}
      </div>

      {/* Tail - Right 32mm (non-printable) */}
      <div className="tail" style={{ 
        width: '32mm', 
        height: '10mm',
        boxSizing: 'border-box'
      }}>
        {/* Tail section - intentionally left blank */}
      </div>
    </div>
  );
};

export default BarcodeLabel; 